/**
 * Геометрия гильотинного раскроя: split свободного пространства, pruning
 * вложенных прямоугольников, допустимые ориентации детали.
 *
 * @see TRD §5.1.2 (split), §5.1.3 (pruning), §5.1.4 (текстура/ориентация)
 */

import type { FreeRect, Orientation } from './types';
import type { Part } from '@pomoshnik/shared';

/** Допуск на сравнения с плавающей точкой (мм). */
const EPS = 1e-6;

/**
 * Допустимые ориентации детали по режиму вращения (контракт `RotationMode`) с
 * учётом упрощённой текстуры §5.3 (CUT-4.4a).
 *
 * - `allow`  → деталь можно класть как есть или повернуть на 90° (нейтральный декор).
 * - `forbid` → только как задано (текстура вдоль длины — вращать нельзя).
 * - `force`  → только повёрнутая на 90°.
 *
 * Текстура (§5.3, вариант A, gate-релаксация): запрет вращения у `forbid`
 * действует, ТОЛЬКО когда текстуру учитываем.
 * - `considerTexture === false` → `forbid` можно вращать ради плотности (`[normal, rotated]`).
 * - `considerTexture === true` ИЛИ `undefined` → `forbid` строго как задано (`[normal]`).
 *   `undefined` сохраняет поведение CUT-4.1 (back-compat: фикстуры поле не задают).
 *
 * На `allow`/`force` текстура не влияет (это не текстурное ограничение).
 *
 * Полный grain детали относительно листа (`rect.grain` + `sheetGrain`, §5.1.4) —
 * отдельный будущий тикет (новые поля контракта + ADR), здесь НЕ реализуется.
 */
export function computeAllowedOrientations(
  rotation: Part['rotation'],
  considerTexture?: boolean,
): Orientation[] {
  switch (rotation) {
    case 'allow':
      return ['normal', 'rotated'];
    case 'force':
      return ['rotated'];
    case 'forbid':
    default:
      return considerTexture === false ? ['normal', 'rotated'] : ['normal'];
  }
}

/**
 * Гильотинное разрезание свободного прямоугольника после размещения детали
 * габаритом `usedW`×`usedH` (включая kerf) в левом-нижнем углу `free`.
 *
 * Возвращает до двух новых свободных прямоугольников — справа и снизу, каждый
 * получен ОДНИМ сквозным резом (L-резы запрещены — форматно-раскроечный станок
 * их не делает). Вырожденные (нулевой/отрицательной площади) куски отбрасываются.
 *
 * @see TRD §5.1.2
 */
export function splitGuillotine(free: FreeRect, usedW: number, usedH: number): FreeRect[] {
  const result: FreeRect[] = [];

  // Прямоугольник справа от размещённой детали (на всю высоту free).
  const rightW = free.w - usedW;
  if (rightW > EPS) {
    result.push({ x: free.x + usedW, y: free.y, w: rightW, h: free.h });
  }

  // Прямоугольник снизу (по координатам — выше по y) от детали (на ширину детали).
  const bottomH = free.h - usedH;
  if (bottomH > EPS) {
    result.push({ x: free.x, y: free.y + usedH, w: usedW, h: bottomH });
  }

  return result;
}

/**
 * Удаление прямоугольников, полностью содержащихся в других (§5.1.3). После
 * split список `freeRects` может содержать вложенные куски — они дублируют
 * пространство и раздувают перебор.
 */
export function pruneFreeRects(rects: FreeRect[]): FreeRect[] {
  return rects.filter(
    (a, i) => !rects.some((b, j) => i !== j && contains(b, a) && !(isSame(a, b) && j > i)),
  );
}

/** Содержит ли `outer` прямоугольник `inner` целиком (с допуском EPS). */
function contains(outer: FreeRect, inner: FreeRect): boolean {
  return (
    inner.x >= outer.x - EPS &&
    inner.y >= outer.y - EPS &&
    inner.x + inner.w <= outer.x + outer.w + EPS &&
    inner.y + inner.h <= outer.y + outer.h + EPS
  );
}

/** Совпадают ли прямоугольники геометрически (для устойчивого дедупа дубликатов). */
function isSame(a: FreeRect, b: FreeRect): boolean {
  return (
    Math.abs(a.x - b.x) < EPS &&
    Math.abs(a.y - b.y) < EPS &&
    Math.abs(a.w - b.w) < EPS &&
    Math.abs(a.h - b.h) < EPS
  );
}

/**
 * Слияние смежных коллинеарных свободных прямоугольников (§5.1.3). Гильотинный
 * split дробит свободное пространство на куски «справа/снизу»; после размещения
 * нескольких деталей соседние свободные куски, делящие общую полную грань,
 * образуют один прямоугольник, но числятся раздельно — это мешает разместить
 * крупную деталь, которая влезла бы в их объединение.
 *
 * Сливаются ТОЛЬКО пары, объединение которых точно прямоугольник (общая полная
 * грань): одинаковые `x`+`w` и смежные по `y`, либо одинаковые `y`+`h` и смежные
 * по `x`. Итерация до неподвижной точки. Результат прогоняется через
 * `pruneFreeRects` (слияние может сделать прежний кусок вложенным).
 */
export function mergeFreeRects(rects: FreeRect[]): FreeRect[] {
  const current = rects.slice();
  let merged = true;
  while (merged) {
    merged = false;
    for (let i = 0; i < current.length && !merged; i++) {
      for (let j = i + 1; j < current.length && !merged; j++) {
        const a = current[i];
        const b = current[j];
        if (a === undefined || b === undefined) continue;
        const union = tryMerge(a, b);
        if (union) {
          current.splice(j, 1);
          current.splice(i, 1, union);
          merged = true;
        }
      }
    }
  }
  return pruneFreeRects(current);
}

/** Объединение двух прямоугольников, если они делят полную общую грань; иначе null. */
function tryMerge(a: FreeRect, b: FreeRect): FreeRect | null {
  // Вертикально смежны: общая вертикаль (x,w совпадают), стыкуются по y.
  if (Math.abs(a.x - b.x) < EPS && Math.abs(a.w - b.w) < EPS) {
    if (Math.abs(a.y + a.h - b.y) < EPS || Math.abs(b.y + b.h - a.y) < EPS) {
      const y = Math.min(a.y, b.y);
      return { x: a.x, y, w: a.w, h: a.h + b.h };
    }
  }
  // Горизонтально смежны: общая горизонталь (y,h совпадают), стыкуются по x.
  if (Math.abs(a.y - b.y) < EPS && Math.abs(a.h - b.h) < EPS) {
    if (Math.abs(a.x + a.w - b.x) < EPS || Math.abs(b.x + b.w - a.x) < EPS) {
      const x = Math.min(a.x, b.x);
      return { x, y: a.y, w: a.w + b.w, h: a.h };
    }
  }
  return null;
}
